/**
 * Os termos de uso e a política de privacidade (docs/spec/termos.md): os endereços públicos, os dados
 * da empresa que os textos citam e a leitura dos textos (`terms.pt.md`, `privacy.en.md`…), que só
 * baixam quando alguém abre um deles.
 */
import type { Language } from '../i18n/index.js';
import { MIN_AGE } from '../shared.js';

export type LegalDoc = 'terms' | 'privacy';
export const LEGAL_DOCS: readonly LegalDoc[] = ['terms', 'privacy'];

/**
 * A empresa que oferece o RPG Desk, citada no começo dos dois textos (o controlador da LGPD). Sem a
 * razão social, os textos falam só do "RPG Desk"; o `dpo` é o nome do encarregado, e sem ele os
 * textos dão só o e-mail de contato. `dmca`: o agente designado no diretório do Copyright Office
 * americano (dmca.copyright.gov), que a seção 8 dos termos mostra com o endereço da sede e o e-mail de
 * contato, iguais aos do registro; sem o número, a seção fala só do e-mail.
 */
export const LEGAL_ENTITY = {
  /** a razão social */
  name: 'Berza Desenvolvimento de Software Ltda',
  /** 00.000.000/0000-00 */
  cnpj: '42.405.487/0001-32',
  /** o endereço da sede, numa linha */
  address: 'Rua Teixeira de Freitas, 490, Santo Antônio, Belo Horizonte – MG, CEP 30350-180, Brasil',
  dpo: '',
  dmca: { number: 'DMCA-1085141', agent: 'Angelo Bercacola', phone: '+55 31 99352-9095' },
};

type Entity = typeof LEGAL_ENTITY;

const PATHS: Record<LegalDoc, Record<'pt' | 'en', string>> = {
  terms: { pt: '/termos', en: '/terms' },
  privacy: { pt: '/privacidade', en: '/privacy' },
};

const short = (language: Language): 'pt' | 'en' => (language === 'en' ? 'en' : 'pt');

/** O endereço público de um texto, no idioma: `/termos`, `/privacy`… */
export function legalPath(doc: LegalDoc, language: Language): string {
  return PATHS[doc][short(language)];
}

/** O texto e o idioma de um endereço (`/termos`, `/terms/`…); null se não é de um deles. */
export function legalRoute(pathname: string): { doc: LegalDoc; language: Language } | null {
  const path = pathname.toLowerCase().replace(/\/+$/, '');
  for (const doc of LEGAL_DOCS) {
    if (path === PATHS[doc].pt) return { doc, language: 'pt-BR' };
    if (path === PATHS[doc].en) return { doc, language: 'en' };
  }
  return null;
}

const CONTACT = '[contato@rpgdesk.com](mailto:contato@rpgdesk.com)';

function operator(entity: Entity, language: Language): string {
  const en = short(language) === 'en';
  if (!entity.name) return en ? 'The Service is provided by RPG Desk ("RPG Desk", "we", "us").' : 'O Serviço é oferecido pelo RPG Desk ("RPG Desk", "nós").';
  const cnpj = entity.cnpj ? (en ? `, registered in Brazil under CNPJ ${entity.cnpj}` : `, inscrita no CNPJ sob o nº ${entity.cnpj}`) : '';
  const address = entity.address ? (en ? `, with its registered office at ${entity.address}` : `, com sede em ${entity.address}`) : '';
  return en
    ? `RPG Desk is provided by **${entity.name}**${cnpj}${address} ("RPG Desk", "we", "us").`
    : `O RPG Desk é oferecido por **${entity.name}**${cnpj}${address} ("RPG Desk", "nós").`;
}

function dataProtectionOfficer(entity: Entity, language: Language): string {
  const en = short(language) === 'en';
  if (entity.dpo) return en ? `Our data protection officer (DPO) is **${entity.dpo}**, at ${CONTACT}.` : `O encarregado pelo tratamento de dados pessoais é **${entity.dpo}**, em ${CONTACT}.`;
  return en ? `To reach our data protection officer (DPO), write to ${CONTACT}.` : `Para falar com o encarregado pelo tratamento de dados pessoais, escreva para ${CONTACT}.`;
}

function dmcaAgent(entity: Entity, language: Language): string {
  const { number, agent, phone } = entity.dmca;
  if (!number) return '';
  const en = short(language) === 'en';
  const who = [agent, entity.name, entity.address].filter(Boolean).join(', ');
  const tel = phone ? (en ? `; phone ${phone}` : `; telefone ${phone}`) : '';
  return en
    ? `**Designated agent** (registration ${number} with the U.S. Copyright Office): ${who}${tel}; e-mail ${CONTACT}.`
    : `**Agente designado** (registro ${number} no Copyright Office dos Estados Unidos): ${who}${tel}; e-mail ${CONTACT}.`;
}

/** Troca `{operador}`, `{encarregado}`, `{agente}` e `{idade}` do texto. */
export function fillLegal(text: string, language: Language, entity: Entity = LEGAL_ENTITY): string {
  return text
    .replaceAll('{operador}', operator(entity, language))
    .replaceAll('{encarregado}', dataProtectionOfficer(entity, language))
    .replaceAll('{agente}', dmcaAgent(entity, language))
    .replaceAll('{idade}', String(MIN_AGE));
}

const SOURCES = import.meta.glob('./*.md', { query: '?raw', import: 'default' }) as Record<string, () => Promise<string>>;

/** O texto pronto (com os dados trocados), baixado na primeira vez. */
export async function loadLegal(doc: LegalDoc, language: Language): Promise<string> {
  const load = SOURCES[`./${doc}.${short(language)}.md`];
  if (!load) throw new Error(`sem o texto ${doc}.${short(language)}`);
  return fillLegal(await load(), language);
}
